import { AutoComplete, Input, Tooltip } from 'antd';
import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import { useRef, useState } from 'react';
import Section from '../console/Section.jsx';

// `defaults` is a flat Record<string, unknown> the transform falls back to (jobType, status,
// externalSource, …). Edited as `key = value` rows with stable ids; values keep their original type
// until touched. externalSource doubles as the close-source default, so we flag it.
const KEY_SUGGESTIONS = ['jobType', 'status', 'externalSource', 'salary', 'level'];

function defaultsToRows(defaults, nextId) {
	return Object.keys(defaults || {}).map((key) => ({ id: nextId(), key, value: defaults[key] }));
}

function rowsToDefaults(rows) {
	const out = {};
	for (const { key, value } of rows) {
		if (key === '' || key == null) continue;
		out[key] = value;
	}
	return out;
}

export default function DefaultsEditor({ defaults, onChange }) {
	const seq = useRef(0);
	const nextId = () => (seq.current += 1);
	const [rows, setRows] = useState(() => defaultsToRows(defaults, nextId));

	const commit = (nextRows) => {
		setRows(nextRows);
		onChange(rowsToDefaults(nextRows));
	};
	const updateRow = (id, partial) => commit(rows.map((r) => (r.id === id ? { ...r, ...partial } : r)));
	const removeRow = (id) => commit(rows.filter((r) => r.id !== id));
	const addRow = () => commit([...rows, { id: nextId(), key: '', value: '' }]);

	return (
		<Section id="sec-defaults" title="Defaults" description="Applied when a field isn't otherwise set.">
			<div>
				{rows.length === 0 && <div className="ei-empty-inline">No defaults set.</div>}
				{rows.map((row) => (
					<div className="js-map-row" key={row.id}>
						<div className="js-map-target js-token">
							<AutoComplete
								value={row.key}
								options={KEY_SUGGESTIONS.map((k) => ({ value: k }))}
								filterOption={(input, option) =>
									String(option.value).toLowerCase().includes(input.toLowerCase())
								}
								onChange={(key) => updateRow(row.id, { key })}
								placeholder="field"
								style={{ width: '100%' }}
							/>
						</div>
						<span className="js-kw" aria-hidden>
							=
						</span>
						<div className="js-map-source">
							<div className="js-token" style={{ flex: 1 }}>
								<Input
									value={row.value == null ? '' : String(row.value)}
									onChange={(e) => updateRow(row.id, { value: e.target.value })}
									placeholder="value"
								/>
							</div>
							{row.key === 'externalSource' && (
								<Tooltip title="Also used as the default Close source for close-missing.">
									<span className="js-badge">close source</span>
								</Tooltip>
							)}
							<Tooltip title="Remove default">
								<button
									type="button"
									className="js-iconbtn js-danger"
									onClick={() => removeRow(row.id)}
									aria-label="Remove default"
								>
									<DeleteOutlined />
								</button>
							</Tooltip>
						</div>
					</div>
				))}
			</div>

			<div className="js-card-foot">
				<button type="button" className="js-linkbtn" onClick={addRow}>
					<PlusOutlined /> Add default
				</button>
				<span className="js-muted" />
			</div>
		</Section>
	);
}
